import { useEffect, useRef, useState } from "react";
import {
  ThumbsUp,
  ThumbsDown,
  CheckCircle2,
  XCircle,
  DollarSign,
  CalendarDays,
  ImageIcon,
  Loader2,
  EyeOff,
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { INTERVIEW, type Recommendation } from "@/lib/constants";
import type { Interview, InterviewQuestion } from "@/lib/types";
import { answerLabels } from "@/lib/utils";
import { useDict, fill } from "@/lib/i18n/context";

type SignedMedia = {
  snapshots: { url: string; created_at: string }[];
  violationSnapshots: { url: string; created_at: string }[];
  identityPhoto: string | null;
  identityVoice: string | null;
  violationCount: number;
};

const recTone: Record<Recommendation, "green" | "blue" | "amber" | "red"> = {
  strong_hire: "green",
  hire: "green",
  maybe: "amber",
  no_hire: "red",
  strong_no_hire: "red",
};

function groupByCategory(questions: InterviewQuestion[]) {
  const groups: { category: string; items: InterviewQuestion[] }[] = [];
  for (const q of [...questions].sort((a, b) => a.order_index - b.order_index)) {
    const cat = q.category || "role";
    const last = groups[groups.length - 1];
    if (last && last.category === cat) last.items.push(q);
    else groups.push({ category: cat, items: [q] });
  }
  return groups;
}

function renderAnswer(q: InterviewQuestion): string | null {
  return answerLabels(q.options, q.answer);
}

function ScoreRing({ score }: { score: number }) {
  const color =
    score >= 75 ? "#059669" : score >= 50 ? "#d97706" : "#dc2626";
  return (
    <div
      className="relative flex h-28 w-28 items-center justify-center rounded-full"
      style={{
        background: `conic-gradient(${color} ${score * 3.6}deg, #e2e8f0 0deg)`,
      }}
    >
      <div className="flex h-20 w-20 flex-col items-center justify-center rounded-full bg-card">
        <span className="text-2xl font-bold">{score}</span>
        <span className="text-xs text-muted">/ 100</span>
      </div>
    </div>
  );
}

export function Results({
  interview,
  questions,
}: {
  interview: Interview;
  questions: InterviewQuestion[];
}) {
  const t = useDict();
  const [media, setMedia] = useState<SignedMedia | null>(null);

  const pending = interview.summary == null && interview.total_score == null;
  const tries = useRef(0);

  // #region agent log
  fetch("http://127.0.0.1:7892/ingest/b0e9c3ac-9fe9-40e4-b61a-a0051cd95f6a", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-Debug-Session-Id": "629ce6",
    },
    body: JSON.stringify({
      sessionId: "629ce6",
      runId: "post-fix",
      hypothesisId: "H1",
      location: "results.tsx:Results:entry",
      message: "Results render entry",
      data: {
        interviewId: interview.id,
        pending,
      },
      timestamp: Date.now(),
    }),
  }).catch(() => {});
  // #endregion

  useEffect(() => {
    fetch(`/api/interviews/${interview.id}/media`, { credentials: "include" })
      .then(async (res) => {
        if (!res.ok) return null;
        return res.json() as Promise<SignedMedia>;
      })
      .then((data) => {
        if (data) setMedia(data);
      })
      .catch(() => {});
  }, [interview.id]);

  useEffect(() => {
    // #region agent log
    fetch("http://127.0.0.1:7892/ingest/b0e9c3ac-9fe9-40e4-b61a-a0051cd95f6a", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Debug-Session-Id": "629ce6",
      },
      body: JSON.stringify({
        sessionId: "629ce6",
        runId: "white-screen",
        hypothesisId: "H1",
        location: "results.tsx:useEffect-deps",
        message: "About to evaluate navigate in deps",
        data: { pending },
        timestamp: Date.now(),
      }),
    }).catch(() => {});
    // #endregion
    if (!pending) return;
    const id = setInterval(async () => {
      tries.current += 1;
      if (tries.current > 40) {
        clearInterval(id);
        return;
      }
      try {
        const res = await fetch(`/api/interviews/${interview.id}/live`, {
          credentials: "include",
          cache: "no-store",
        });
        if (!res.ok) return;
        const data = await res.json();
        if (data.evaluated) {
          clearInterval(id);
          window.location.reload();
        }
      } catch {
        /* ignore */
      }
    }, INTERVIEW.LIVE_POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [pending, interview.id]);

  const rec = interview.recommendation as Recommendation | null;

  return (
    <div className="space-y-4">
      {pending && (
        <div className="flex items-center gap-3 rounded-xl border border-brand-100 bg-brand-50 px-4 py-3 text-sm font-medium text-brand-700">
          <Loader2 className="h-4 w-4 animate-spin" />
          {t.results.evaluating}
        </div>
      )}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-wrap items-center gap-6">
            <ScoreRing score={interview.total_score ?? 0} />
            <div className="flex-1 space-y-2">
              <div className="flex items-center gap-2">
                {rec && (
                  <Badge tone={recTone[rec]}>
                    {(rec === "strong_hire" || rec === "hire") && (
                      <ThumbsUp className="h-3 w-3" />
                    )}
                    {(rec === "no_hire" || rec === "strong_no_hire") && (
                      <ThumbsDown className="h-3 w-3" />
                    )}
                    {t.results.recommendations[rec]}
                  </Badge>
                )}
              </div>
              <p className="text-sm text-foreground/80">
                {interview.summary ?? t.results.summaryNone}
              </p>
              <div className="flex flex-wrap gap-4 pt-1 text-sm">
                {interview.expected_salary && (
                  <span className="flex items-center gap-1 text-muted">
                    <DollarSign className="h-4 w-4" /> {interview.expected_salary}
                  </span>
                )}
                {interview.joining_date && (
                  <span className="flex items-center gap-1 text-muted">
                    <CalendarDays className="h-4 w-4" /> {interview.joining_date}
                  </span>
                )}
                <span
                  className={`flex items-center gap-1 ${
                    (media?.violationCount ?? interview.violation_count) > 0
                      ? "font-medium text-amber-600"
                      : "text-muted"
                  }`}
                >
                  <EyeOff className="h-4 w-4" />
                  {t.results.integrityLeft}:{" "}
                  {fill(t.results.integrityTimes, {
                    count: String(
                      media?.violationCount ?? interview.violation_count ?? 0,
                    ),
                  })}
                </span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="pt-6">
          <h3 className="mb-3 flex items-center gap-2 font-semibold">
            <EyeOff className="h-4 w-4 text-amber-600" />
            {t.results.integrityTitle}
          </h3>
          <p className="mb-4 text-sm text-foreground/80">
            {t.results.integrityLeft} —{" "}
            <strong>
              {fill(t.results.integrityTimes, {
                count: String(
                  media?.violationCount ?? interview.violation_count ?? 0,
                ),
              })}
            </strong>
            {(media?.violationCount ?? interview.violation_count ?? 0) === 0
              ? ` · ${t.results.integrityNone}`
              : null}
          </p>
          <h4 className="mb-2 text-sm font-medium text-muted">
            {t.results.integrityShots}
            {media?.violationSnapshots?.length
              ? ` (${media.violationSnapshots.length})`
              : ""}
          </h4>
          {media?.violationSnapshots && media.violationSnapshots.length > 0 ? (
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
              {media.violationSnapshots.map((s, i) => (
                <a
                  key={`${s.url}-${i}`}
                  href={s.url}
                  target="_blank"
                  rel="noreferrer"
                  className="group relative overflow-hidden rounded-lg border border-border"
                >
                  <img
                    src={s.url}
                    alt={`Leave screenshot ${i + 1}`}
                    className="aspect-video w-full object-cover transition group-hover:opacity-90"
                  />
                  <span className="absolute inset-x-0 bottom-0 bg-black/55 px-2 py-1 text-[10px] text-white">
                    {new Date(s.created_at).toLocaleString()}
                  </span>
                </a>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted">{t.results.integrityNoShots}</p>
          )}
        </CardContent>
      </Card>

      <div className="grid gap-4 sm:grid-cols-2">
        <Card>
          <CardContent className="pt-6">
            <h3 className="mb-3 flex items-center gap-2 font-semibold text-emerald-700">
              <CheckCircle2 className="h-4 w-4" /> {t.results.strengths}
            </h3>
            <ul className="space-y-2 text-sm text-foreground/80">
              {(interview.strengths ?? []).map((s, i) => (
                <li key={i} className="flex gap-2">
                  <span className="text-emerald-500">+</span> {s}
                </li>
              ))}
              {(!interview.strengths || interview.strengths.length === 0) && (
                <li className="text-muted">{t.results.noneNoted}</li>
              )}
            </ul>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <h3 className="mb-3 flex items-center gap-2 font-semibold text-red-700">
              <XCircle className="h-4 w-4" /> {t.results.concerns}
            </h3>
            <ul className="space-y-2 text-sm text-foreground/80">
              {(interview.concerns ?? []).map((s, i) => (
                <li key={i} className="flex gap-2">
                  <span className="text-red-500">-</span> {s}
                </li>
              ))}
              {(!interview.concerns || interview.concerns.length === 0) && (
                <li className="text-muted">{t.results.noneNoted}</li>
              )}
            </ul>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardContent className="pt-6">
          <h3 className="mb-3 font-semibold">{t.results.identityVerification}</h3>
          <div className="flex flex-wrap items-center gap-6">
            {media?.identityPhoto ? (
              <img
                src={media.identityPhoto}
                alt="Identity"
                className="h-32 w-32 rounded-xl object-cover"
              />
            ) : (
              <div className="flex h-32 w-32 items-center justify-center rounded-xl bg-surface-hover text-muted">
                <ImageIcon className="h-6 w-6" />
              </div>
            )}
            {media?.identityVoice ? (
              <div className="min-w-0 flex-1">
                <p className="mb-2 text-sm text-muted">{t.results.voiceSample}</p>
                <video
                  controls
                  playsInline
                  src={media.identityVoice}
                  className="max-h-40 w-full max-w-sm rounded-xl bg-slate-900"
                />
              </div>
            ) : (
              <p className="text-sm text-muted">{t.results.noVoice}</p>
            )}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="pt-6">
          <h3 className="mb-3 font-semibold">{t.results.answersScores}</h3>
          <div className="space-y-6">
            {groupByCategory(questions).map((group) => (
              <div key={group.category}>
                <div className="mb-2 flex items-center gap-2">
                  <span className="h-4 w-1 rounded-full bg-brand-500" />
                  <h4 className="text-sm font-semibold uppercase tracking-wide text-brand-700">
                    {t.categories[group.category] ?? group.category}
                  </h4>
                  <span className="text-xs text-muted">({group.items.length})</span>
                </div>
                <div className="space-y-3">
                  {group.items.map((q) => (
                    <div key={q.id} className="rounded-xl border border-border p-4">
                      <div className="flex items-start justify-between gap-3">
                        <p className="font-medium">{q.prompt}</p>
                        {q.ai_score != null && (
                          <span className="shrink-0 rounded-lg bg-surface-hover px-2 py-1 text-sm font-semibold">
                            {q.ai_score}
                          </span>
                        )}
                      </div>
                      <p className="mt-2 text-sm text-foreground/80">
                        <span className="text-muted">{t.results.answerLabel} </span>
                        {q.skipped ? (
                          <em className="text-muted">{t.results.skipped}</em>
                        ) : (
                          renderAnswer(q) || (
                            <em className="text-muted">{t.results.noAnswer}</em>
                          )
                        )}
                      </p>
                      {q.ai_feedback && (
                        <p className="mt-2 rounded-lg bg-surface p-2 text-sm text-muted">
                          {q.ai_feedback}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {media && media.snapshots.length > 0 && (
        <Card>
          <CardContent className="pt-6">
            <h3 className="mb-3 font-semibold">
              {t.results.monitoringSnapshots} ({media.snapshots.length})
            </h3>
            <div className="grid grid-cols-3 gap-2 sm:grid-cols-6">
              {media.snapshots.map((s, i) => (
                <img
                  key={i}
                  src={s.url}
                  alt={`Snapshot ${i + 1}`}
                  className="aspect-video w-full rounded-lg object-cover"
                />
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
